/**
 * 🔧 ADMIN BOOKING DETAILS MODAL
 * Comprehensive booking management interface for admins
 * Features: View, Modify, Extend, Cancel, Refund, History, Notifications
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Tabs,
  Tab,
  TextField,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  DirectionsCar as VehicleIcon,
  Event as EventIcon,
  MonetizationOn as MoneyIcon,
  History as HistoryIcon,
  Notifications as NotificationsIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  AttachMoney as RefundIcon,
  Schedule as ExtendIcon,
  CheckCircle as SuccessIcon,
  Send as SendIcon,
  VerifiedUser as VerifiedUserIcon,
  CompareArrows as CompareArrowsIcon,
  Print,
} from '@mui/icons-material';
import { bookingServiceV2 } from '@/services/bookingService.v2';
import { adminNotificationService } from '@/services/adminNotificationService';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { safeFormatDate } from '@/utils/filterUtils';
import { CheckinCheckoutSection } from '@/components/disputes/CheckinCheckoutSection';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { CancellationDialog } from '@/components/bookings/CancellationDialog';
import { RefundDialog } from '@/components/bookings/RefundDialog';
import { printReceipt } from '@/utils/printReceipt';
import type { BookingV2SingleResponse } from '@/types/booking';

interface AdminBookingDetailsModalProps {
  open: boolean;
  onClose: () => void;
  bookingId: string | null;
  onBookingUpdated?: () => void;
  initialTab?: number; // Auto-navigate to specific tab (0-7)
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

export const AdminBookingDetailsModal: React.FC<AdminBookingDetailsModalProps> = ({
  open,
  onClose,
  bookingId,
  onBookingUpdated,
  initialTab = 0,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [booking, setBooking] = useState<any | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Extend booking state
  const [extendForm, setExtendForm] = useState({
    newEndDate: '',
    reason: '',
    additionalCost: '', // empty = the server prices the extension
  });
  const [isExtending, setIsExtending] = useState(false);

  // Modify booking state
  const [modifyForm, setModifyForm] = useState({
    startDate: '',
    endDate: '',
    pickupLocation: '',
    dropoffLocation: '',
    notes: '',
    reason: '',
  });
  const [isModifying, setIsModifying] = useState(false);

  // Cancel and refund are now handled by dedicated dialogs

  // Notification state
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    type: 'booking_update',
  });
  const [isSendingNotification, setIsSendingNotification] = useState(false);

  // Check-in/Checkout state
  const [checkinCheckoutData, setCheckinCheckoutData] = useState<any>(null);
  const [isLoadingCheckinCheckout, setIsLoadingCheckinCheckout] = useState(false);

  // Dialog states for reusable modals
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [extendConfirmOpen, setExtendConfirmOpen] = useState(false);
  const [modifyConfirmOpen, setModifyConfirmOpen] = useState(false);

  // Past date tracking for modify form
  const [modifyFormHasPastDate, setModifyFormHasPastDate] = useState(false);

  // Load booking details
  useEffect(() => {
    if (open && bookingId) {
      loadBookingDetails();
    }
  }, [open, bookingId]);

  // Set initial tab when modal opens
  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
    }
  }, [open, initialTab]);

  // Load additional data when tabs change
  useEffect(() => {
    if (open && bookingId && booking) {
      if (activeTab === 3) loadHistory();
      if (activeTab === 4) loadNotifications();
      if (activeTab === 6) loadCheckinCheckoutData(); // Tab 7 (index 6)
    }
  }, [activeTab, open, bookingId]);

  // Check if modify form has past start date
  useEffect(() => {
    if (modifyForm.startDate) {
      const now = new Date();
      now.setHours(0, 0, 0, 0); // Reset to start of day for fair comparison
      const selectedDate = new Date(modifyForm.startDate);
      setModifyFormHasPastDate(selectedDate < now);
    } else {
      setModifyFormHasPastDate(false);
    }
  }, [modifyForm.startDate]);

  const loadBookingDetails = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const v2Booking = await bookingServiceV2.getBookingById(bookingId!);
      if (v2Booking) {
        setBooking(v2Booking);
        initializeForms(v2Booking);
      } else {
        setError('Booking not found');
      }
    } catch (err) {
      setError('Failed to load booking details');
      console.error('Error loading booking:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to convert ISO datetime to datetime-local format (yyyy-MM-ddThh:mm)
  const formatForDatetimeLocal = (isoString: string): string => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (e) {
      return '';
    }
  };

  const initializeForms = (bookingData: any) => {
    // V2 API returns nested structure with ISO datetime strings
    const startDate = bookingData.booking?.startDate || '';
    const endDate = bookingData.booking?.endDate || '';
    const pickupLocation = bookingData.location?.pickup || '';
    const dropoffLocation = bookingData.location?.dropoff || '';
    const amount = bookingData.payment?.amount || 0;
    const notes = typeof bookingData.notes === 'object' ? bookingData.notes?.note || '' : bookingData.notes || '';

    setModifyForm({
      startDate: formatForDatetimeLocal(startDate),
      endDate: formatForDatetimeLocal(endDate),
      pickupLocation,
      dropoffLocation,
      notes,
      reason: '',
    });

    setExtendForm({
      newEndDate: formatForDatetimeLocal(endDate),
      reason: '',
      additionalCost: '',
    });
  };

  const loadHistory = async () => {
    try {
      const result = await bookingServiceV2.getBookingHistory(bookingId!);
      if (result.success && result.data) {
        setHistory(result.data);
      }
    } catch (error) {
      console.error('Error loading history:', error);
      setHistory([]);
    }
  };

  const loadNotifications = async () => {
    try {
      const result = await adminNotificationService.getNotificationsByBooking(bookingId!);
      if (result.success && result.data) {
        setNotifications(result.data.notifications);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      setNotifications([]);
    }
  };

  const loadCheckinCheckoutData = async () => {
    if (!bookingId) return;
    setIsLoadingCheckinCheckout(true);
    try {
      // V2 API: Use dedicated checkin-checkout endpoint
      const result = await bookingServiceV2.getCheckinCheckoutDetails(bookingId);
      if (result.success && result.data) {
        setCheckinCheckoutData(result.data);
      } else {
        setCheckinCheckoutData(null);
      }
    } catch (error) {
      console.error('❌ Error loading check-in/checkout data:', error);
      setCheckinCheckoutData(null);
    } finally {
      setIsLoadingCheckinCheckout(false);
    }
  };

  // ========================================
  // DATE VALIDATION UTILITIES
  // ========================================

  const validateDates = (startDate: string, endDate: string): { valid: boolean; error?: string } => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return { valid: false, error: 'Invalid date format' };
    }

    // Check if end date is before start date
    if (end <= start) {
      return { valid: false, error: 'End date must be after start date' };
    }

    return { valid: true };
  };

  const validateExtensionDate = (currentEndDate: string, newEndDate: string): { valid: boolean; error?: string } => {
    const current = new Date(currentEndDate);
    const newEnd = new Date(newEndDate);

    // Check if date is valid
    if (isNaN(newEnd.getTime())) {
      return { valid: false, error: 'Invalid date format' };
    }

    // Check if new end date is before or equal to current end date
    if (newEnd <= current) {
      return { valid: false, error: 'New end date must be after current end date' };
    }

    return { valid: true };
  };

  // ========================================
  // ACTION HANDLERS
  // ========================================

  const handleExtend = async () => {
    if (!extendForm.newEndDate || !extendForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate extension date
    const validation = validateExtensionDate(booking.booking?.endDate, extendForm.newEndDate);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    setIsExtending(true);
    try {
      // V2 API: Use dedicated /extend endpoint with availability checking and cost recalculation
      const result = await bookingServiceV2.extendBooking(bookingId!, {
        newEndDate: extendForm.newEndDate,
        // Empty: the server prices the extension. A typed amount (0 allowed) is recorded as an override
        additionalCost: extendForm.additionalCost === '' ? undefined : Number(extendForm.additionalCost),
        adminNotes: extendForm.reason
      });

      // Show success with cost details if provided
      if (result.additionalCost) {
        toast.success(
          `Booking extended successfully! Additional cost: ₦${result.additionalCost.toLocaleString()}`
        );
      } else {
        toast.success('Booking extended successfully');
      }

      loadBookingDetails();
      onBookingUpdated?.();
      setExtendForm({ newEndDate: '', reason: '', additionalCost: '' });
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to extend booking';
      toast.error(errorMessage);
      console.error('Error extending booking:', error);
    } finally {
      setIsExtending(false);
    }
  };

  const handleModify = async () => {
    if (!modifyForm.reason) {
      toast.error('Please provide a reason for modification');
      return;
    }

    // Validate dates if they are being modified
    if (modifyForm.startDate && modifyForm.endDate) {
      const validation = validateDates(modifyForm.startDate, modifyForm.endDate);
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }
    }

    setIsModifying(true);
    try {
      // V2 API: Use dedicated /modify endpoint with availability checking and cost recalculation
      const modifyData: any = {
        adminNotes: modifyForm.reason // Store reason in admin notes
      };

      if (modifyForm.startDate) modifyData.startDate = modifyForm.startDate;
      if (modifyForm.endDate) modifyData.endDate = modifyForm.endDate;
      if (modifyForm.pickupLocation) modifyData.pickupLocation = modifyForm.pickupLocation;
      if (modifyForm.dropoffLocation) modifyData.dropoffLocation = modifyForm.dropoffLocation;
      if (modifyForm.notes) modifyData.specialRequests = modifyForm.notes;

      const result = await bookingServiceV2.modifyBooking(bookingId!, modifyData);

      // Show success message with cost details if cost changed
      if (result.costChanged) {
        toast.success(
          `Booking modified successfully! ${result.autoCalculated ? 'Cost auto-recalculated' : 'Cost updated'}: ₦${result.newTotalAmount?.toLocaleString()}`
        );
      } else {
        toast.success('Booking modified successfully');
      }

      loadBookingDetails();
      onBookingUpdated?.();
      setActiveTab(0); // Switch to details tab
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to modify booking';
      toast.error(errorMessage);
      console.error('Error modifying booking:', error);
    } finally {
      setIsModifying(false);
    }
  };

  const handleCancelClick = () => {
    setCancelDialogOpen(true);
  };

  const handleCancelSuccess = (suggestedRefund?: any) => {
    if (suggestedRefund && suggestedRefund.totalRefund > 0) {
      toast.success(
        `Booking cancelled successfully! Suggested refund: ₦${suggestedRefund.totalRefund.toLocaleString()}`,
        { duration: 5000 }
      );
    } else {
      toast.success('Booking cancelled successfully');
    }
    loadBookingDetails();
    onBookingUpdated?.();
    setActiveTab(0);
  };

  const handleRefundClick = () => {
    setRefundDialogOpen(true);
  };

  const handleRefundSuccess = () => {
    toast.success('Refund processed successfully');
    loadBookingDetails();
    onBookingUpdated?.();
  };

  const handleSendNotification = async () => {
    if (!notificationForm.title || !notificationForm.message) {
      toast.error('Please fill in notification details');
      return;
    }

    if (!booking?.customer?.userId && !booking?.userId) {
      toast.error('Customer ID not found');
      return;
    }

    setIsSendingNotification(true);
    try {
      const result = await adminNotificationService.sendToUser({
        userId: booking.customer?.userId || booking.userId,
        title: notificationForm.title,
        message: notificationForm.message,
        type: notificationForm.type,
      });

      if (result.success) {
        toast.success('Notification sent successfully');
        setNotificationForm({ title: '', message: '', type: 'booking_update' });
        loadNotifications();
      } else {
        toast.error(result.error || 'Failed to send notification');
      }
    } catch (error) {
      toast.error('Failed to send notification');
      console.error('Error sending notification:', error);
    } finally {
      setIsSendingNotification(false);
    }
  };

  const getStatusColor = (status: string) => {
    const statusMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
      confirmed: 'success',
      completed: 'success',
      pending: 'warning',
      cancelled: 'error',
      active: 'info',
    };
    return statusMap[status?.toLowerCase()] || 'default';
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: booking?.currency || 'NGN',
    }).format(amount || 0);
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight="bold">
            Booking Details: {booking?.bookingRef || bookingId}
          </Typography>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : booking ? (
          <>
            <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ mb: 2 }}>
              <Tab label="Details" icon={<PersonIcon />} iconPosition="start" />
              <Tab label="Extend" icon={<ExtendIcon />} iconPosition="start" />
              <Tab label="Modify" icon={<EditIcon />} iconPosition="start" />
              <Tab label="History" icon={<HistoryIcon />} iconPosition="start" />
              <Tab label="Notifications" icon={<NotificationsIcon />} iconPosition="start" />
              <Tab label="Actions" icon={<CancelIcon />} iconPosition="start" />
              <Tab label="Check-in/Check-out" icon={<CompareArrowsIcon />} iconPosition="start" />
            </Tabs>

            {/* Tab 0: Details */}
            <TabPanel value={activeTab} index={0}>
              <Grid container spacing={3}>
                {/* Customer Card */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon /> Customer
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar>
                          {(booking.customer?.name || booking.customerName || 'U').charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {booking.customer?.name || booking.customerName || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {booking.customer?.email || booking.customerEmail || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {booking.customer?.phone || booking.customerPhone || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Vehicle Details */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VehicleIcon /> Vehicle
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">{booking.vehicle?.name || 'N/A'}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {booking.vehicle?.year || 'N/A'} - {booking.vehicle?.licensePlate || 'N/A'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Booking Info */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EventIcon /> Booking Details
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Start Date</Typography>
                          <Typography>{safeFormatDate(booking.booking?.startDate, 'PPpp')}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">End Date</Typography>
                          <Typography>{safeFormatDate(booking.booking?.endDate, 'PPpp')}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Booking Type</Typography>
                          <Typography>{booking.serviceType || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Status</Typography>
                          <Chip label={booking.status || 'pending'} color={getStatusColor(booking.status)} size="small" />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Booking ID</Typography>
                          <Typography>{booking.bookingRef || booking.id || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Service Category</Typography>
                          <Typography>{booking.booking?.entityType || 'N/A'}</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Pricing */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MoneyIcon /> Pricing & Payment
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}><Typography>Rental Amount:</Typography></Grid>
                        <Grid item xs={6}><Typography fontWeight="bold">₦{(booking.payment?.amount || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography></Grid>

                        {/* Security Deposit Section - V2 nested structure */}
                        {(() => {
                          const deposit = booking.payment?.securityDeposit || 0;
                          const depositStatus = booking.payment?.securityDepositStatus;
                          const heldAt = booking.payment?.securityDepositHeldAt;
                          const releasedAt = booking.payment?.securityDepositReleasedAt;
                          const rentalAmount = booking.payment?.amount || 0;

                          if (!deposit) return null;

                          return (
                            <>
                              <Grid item xs={6}><Typography>Security Deposit:</Typography></Grid>
                              <Grid item xs={6}><Typography fontWeight="bold">₦{deposit.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography></Grid>

                              <Grid item xs={6}><Typography>Total Paid:</Typography></Grid>
                              <Grid item xs={6}><Typography fontWeight="bold" color="primary.main">₦{(rentalAmount + deposit).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography></Grid>

                              <Grid item xs={12} sx={{ mt: 1, mb: 1 }}>
                                <Divider />
                              </Grid>

                              <Grid item xs={6}><Typography fontWeight="600">Deposit Status:</Typography></Grid>
                              <Grid item xs={6}>
                                <Chip
                                  label={
                                    depositStatus === 'pending' ? '⏳ Pending' :
                                    depositStatus === 'held' ? '🟡 Held' :
                                    depositStatus === 'released' ? '🟢 Released' :
                                    depositStatus === 'refunded' ? '🟢 Refunded' :
                                    depositStatus === 'forfeited' ? '🔴 Forfeited' :
                                    '⚪ Unknown'
                                  }
                                  size="small"
                                  color={
                                    depositStatus === 'released' || depositStatus === 'refunded' ? 'success' :
                                    depositStatus === 'held' ? 'warning' :
                                    depositStatus === 'pending' ? 'info' :
                                    depositStatus === 'forfeited' ? 'error' :
                                    'default'
                                  }
                                />
                              </Grid>

                              {heldAt && (
                                <>
                                  <Grid item xs={6}><Typography variant="body2" color="text.secondary">Held Since:</Typography></Grid>
                                  <Grid item xs={6}><Typography variant="body2">{safeFormatDate(heldAt, 'MMM dd, yyyy HH:mm')}</Typography></Grid>
                                </>
                              )}

                              {releasedAt && (
                                <>
                                  <Grid item xs={6}><Typography variant="body2" color="text.secondary">Released On:</Typography></Grid>
                                  <Grid item xs={6}><Typography variant="body2" color="success.main">{safeFormatDate(releasedAt, 'MMM dd, yyyy HH:mm')}</Typography></Grid>
                                </>
                              )}

                              {!releasedAt && depositStatus === 'held' && (
                                <>
                                  <Grid item xs={6}><Typography variant="body2" color="text.secondary">Refund Status:</Typography></Grid>
                                  <Grid item xs={6}><Typography variant="body2" color="warning.main">Not yet refunded</Typography></Grid>
                                </>
                              )}
                            </>
                          );
                        })()}

                        <Grid item xs={12} sx={{ mt: 1, mb: 1 }}>
                          <Divider />
                        </Grid>

                        <Grid item xs={6}><Typography>Currency:</Typography></Grid>
                        <Grid item xs={6}><Typography>NGN</Typography></Grid>
                        <Grid item xs={6}><Typography>Payment Method:</Typography></Grid>
                        <Grid item xs={6}><Typography>{booking.payment?.method || 'N/A'}</Typography></Grid>
                        <Grid item xs={6}><Typography>Payment Status:</Typography></Grid>
                        <Grid item xs={6}><Chip label={booking.payment?.status || booking.paymentStatus || 'pending'} size="small" color={(booking.payment?.status || booking.paymentStatus) === 'paid' ? 'success' : 'warning'} /></Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Notes */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Notes
                      </Typography>
                      <Typography variant="body2">
                        {typeof booking.notes === 'object' && booking.notes !== null
                          ? booking.notes.note || 'No additional notes for this booking.'
                          : booking.notes || 'No additional notes for this booking.'}
                      </Typography>
                      {typeof booking.notes === 'object' && booking.notes !== null && (
                        <Box sx={{ mt: 2 }}>
                          {booking.notes.isFirstTimeUser && (
                            <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
                              🎉 First-time user discount applied: ₦{booking.notes.firstTimeDiscount?.toLocaleString()}
                            </Typography>
                          )}
                          {booking.notes.totalPaid && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Total paid (including deposit): ₦{booking.notes.totalPaid?.toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Tab 1: Extend Booking */}
            <TabPanel value={activeTab} index={1}>
              <Alert severity="info" sx={{ mb: 2 }}>
                ⏰ <strong>Date Validation:</strong> The new end date must be after the current end date ({formatDate(booking?.endDate)}).
              </Alert>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Extend Booking</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Extend the booking end date and adjust pricing if needed
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="New End Date"
                        type="datetime-local"
                        value={extendForm.newEndDate}
                        onChange={(e) => setExtendForm({ ...extendForm, newEndDate: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        helperText="Must be after current end date"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Additional Cost"
                        type="number"
                        value={extendForm.additionalCost}
                        onChange={(e) => setExtendForm({ ...extendForm, additionalCost: e.target.value })}
                        placeholder="Server price"
                        helperText="Leave empty to use the server price, or enter an amount to override it"
                        inputProps={{ min: 0, step: 'any' }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">{booking.currency || 'NGN'}</InputAdornment>
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Reason for Extension *"
                        multiline
                        rows={3}
                        value={extendForm.reason}
                        onChange={(e) => setExtendForm({ ...extendForm, reason: e.target.value })}
                        placeholder="Enter reason for extending this booking..."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        onClick={handleExtend}
                        disabled={isExtending}
                        startIcon={isExtending ? <CircularProgress size={20} /> : <ExtendIcon />}
                        fullWidth
                      >
                        {isExtending ? 'Extending...' : 'Extend Booking'}
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </TabPanel>

            {/* Tab 2: Modify Booking */}
            <TabPanel value={activeTab} index={2}>
              <Alert severity="info" sx={{ mb: 2 }}>
                ℹ️ <strong>Note:</strong> End date must be after start date. As an admin, you can create bookings with past dates if needed.
              </Alert>
              {modifyFormHasPastDate && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  ⚠️ <strong>Past Date Selected:</strong> You are modifying the booking to have a start date in the past.
                  This should only be done for historical records or special circumstances.
                </Alert>
              )}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Modify Booking</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Update booking details (dates, locations, etc.)
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="datetime-local"
                        value={modifyForm.startDate}
                        onChange={(e) => setModifyForm({ ...modifyForm, startDate: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        helperText="Select booking start date"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="End Date"
                        type="datetime-local"
                        value={modifyForm.endDate}
                        onChange={(e) => setModifyForm({ ...modifyForm, endDate: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: modifyForm.startDate,
                        }}
                        helperText="Must be after start date"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Pickup Location"
                        value={modifyForm.pickupLocation}
                        onChange={(e) => setModifyForm({ ...modifyForm, pickupLocation: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Dropoff Location"
                        value={modifyForm.dropoffLocation}
                        onChange={(e) => setModifyForm({ ...modifyForm, dropoffLocation: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Notes"
                        multiline
                        rows={2}
                        value={modifyForm.notes}
                        onChange={(e) => setModifyForm({ ...modifyForm, notes: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Reason for Modification *"
                        multiline
                        rows={3}
                        value={modifyForm.reason}
                        onChange={(e) => setModifyForm({ ...modifyForm, reason: e.target.value })}
                        placeholder="Enter reason for modifying this booking..."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        onClick={handleModify}
                        disabled={isModifying}
                        startIcon={isModifying ? <CircularProgress size={20} /> : <EditIcon />}
                        fullWidth
                      >
                        {isModifying ? 'Modifying...' : 'Modify Booking'}
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </TabPanel>

            {/* Tab 3: History */}
            <TabPanel value={activeTab} index={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Modification History</Typography>
                  {history.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No modification history available
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Action</TableCell>
                            <TableCell>Details</TableCell>
                            <TableCell>Modified By</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {history.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>{formatDate(item.timestamp || item.createdAt)}</TableCell>
                              <TableCell>
                                <Chip label={item.action || item.type} size="small" />
                              </TableCell>
                              <TableCell>{item.details || item.reason || 'N/A'}</TableCell>
                              <TableCell>{item.modifiedBy || item.adminName || 'Admin'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </TabPanel>

            {/* Tab 4: Notifications */}
            <TabPanel value={activeTab} index={4}>
              <Grid container spacing={2}>
                {/* Send New Notification */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Send Notification</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Title"
                            value={notificationForm.title}
                            onChange={(e) => setNotificationForm({ ...notificationForm, title: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Message"
                            multiline
                            rows={3}
                            value={notificationForm.message}
                            onChange={(e) => setNotificationForm({ ...notificationForm, message: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            onClick={handleSendNotification}
                            disabled={isSendingNotification}
                            startIcon={isSendingNotification ? <CircularProgress size={20} /> : <SendIcon />}
                            fullWidth
                          >
                            {isSendingNotification ? 'Sending...' : 'Send Notification'}
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Notification History */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Notification History</Typography>
                      {notifications.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No notifications sent for this booking
                        </Typography>
                      ) : (
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {notifications.map((notif, index) => (
                                <TableRow key={index}>
                                  <TableCell>{formatDate(notif.sentAt || notif.createdAt)}</TableCell>
                                  <TableCell>{notif.title}</TableCell>
                                  <TableCell>
                                    <Chip label={notif.type} size="small" variant="outlined" />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={notif.isRead ? 'Read' : 'Unread'}
                                      size="small"
                                      color={notif.isRead ? 'success' : 'default'}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Tab 5: Actions (Cancel & Refund) */}
            <TabPanel value={activeTab} index={5}>
              <Grid container spacing={3}>
                {/* Cancel Booking */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="error">Cancel Booking</Typography>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        This action cannot be undone. The customer will be notified.
                      </Alert>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Cancel this booking with automatic refund calculation and customer notification.
                      </Typography>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={handleCancelClick}
                        startIcon={<CancelIcon />}
                        fullWidth
                        size="large"
                      >
                        Cancel Booking
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Process Refund */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Process Refund</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Process a refund for this booking (partial or full refund available).
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={handleRefundClick}
                        startIcon={<RefundIcon />}
                        fullWidth
                        size="large"
                      >
                        Process Refund
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Print Receipt */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Print Receipt</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Generate and print a payment receipt for this booking.
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => {
                          if (booking) {
                            try {
                              printReceipt(booking as any);
                            } catch (error) {
                              console.error('Error printing receipt:', error);
                              toast.error('Failed to print receipt. Please allow popups.');
                            }
                          }
                        }}
                        startIcon={<Print />}
                        fullWidth
                        size="large"
                      >
                        Print Receipt
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Tab 6: Check-in/Check-out */}
            <TabPanel value={activeTab} index={6}>
              {isLoadingCheckinCheckout ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>Loading check-in/checkout data...</Typography>
                </Box>
              ) : checkinCheckoutData ? (
                <CheckinCheckoutSection
                  data={checkinCheckoutData}
                  onResolveDispute={async (resolution) => {
                    // Optional: Add resolution handling if needed from booking view
                    toast('Check-in/checkout comparison view - dispute resolution available from disputes page');
                  }}
                />
              ) : (
                <Alert severity="info">
                  No check-in/checkout data available for this booking. This data is only available after the rental period when check-in and check-out have been completed.
                </Alert>
              )}
            </TabPanel>
          </>
        ) : (
          <Typography>No booking data available</Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Reusable Confirmation Dialogs */}
      {bookingId && (
        <>
          <CancellationDialog
            open={cancelDialogOpen}
            onClose={() => setCancelDialogOpen(false)}
            bookingId={bookingId}
            onSuccess={handleCancelSuccess}
          />

          <RefundDialog
            open={refundDialogOpen}
            onClose={() => setRefundDialogOpen(false)}
            booking={booking ? {
              id: bookingId,
              totalAmount: booking.payment?.amount?.toString() || '0',
              bookingNumber: booking.bookingRef || bookingId
            } as any : null}
            onSuccess={handleRefundSuccess}
          />
        </>
      )}
    </Dialog>
  );
};
